import { Avatar, Badge, cn } from '@nexo/ui';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { PriorityIcon, StatusIcon } from './TaskIcons';
import { TaskRow } from './TaskRow';
import type { TaskGroup } from './taskUtils';

/** Collapsible task groups with a header per group. */
export function TaskGroupList({
  groups,
  collapsedByDefault = [],
  showChannel = true,
}: {
  groups: TaskGroup[];
  collapsedByDefault?: string[];
  showChannel?: boolean;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(collapsedByDefault));
  const toggle = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => {
        const isCollapsed = collapsed.has(group.key);
        return (
          <section key={group.key}>
            <button
              type="button"
              aria-expanded={!isCollapsed}
              onClick={() => toggle(group.key)}
              className="sticky top-0 z-10 flex w-full items-center gap-2 rounded-lg bg-surface/95 px-2 py-1.5 text-left backdrop-blur"
            >
              <ChevronRight
                className={cn(
                  'size-3.5 text-subtle transition-transform',
                  !isCollapsed && 'rotate-90',
                )}
              />
              <GroupIcon group={group} />
              <span
                className={cn(
                  'text-[13px] font-semibold',
                  group.tone === 'danger' && 'text-danger',
                )}
              >
                {group.label}
              </span>
              <Badge tone={group.tone ?? 'neutral'}>{group.tasks.length}</Badge>
            </button>
            {!isCollapsed && (
              <div className="mt-0.5 flex flex-col">
                {group.tasks.map((task) => (
                  <TaskRow key={task.id} task={task} showChannel={showChannel} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function GroupIcon({ group }: { group: TaskGroup }) {
  if (group.status) return <StatusIcon status={group.status} />;
  if (group.priority) return <PriorityIcon priority={group.priority} showNormal />;
  if (group.person)
    return (
      <Avatar
        id={group.person.id}
        name={group.person.displayName}
        src={group.person.avatarUrl}
        size="xs"
      />
    );
  return null;
}
